import { useMemo } from "react";
import { Trash2, Plus, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIP_SERVICES } from "@/lib/tips";

// Dropdown options: the known tip services + a generic "Other".
export const TIP_OPTIONS = [...TIP_SERVICES.map((s) => [s.key, s.label]), ["other", "Other / Link"]];
const PLACEHOLDER = { ...Object.fromEntries(TIP_SERVICES.map((s) => [s.key, s.placeholder])), other: "https://your-tip-link" };
// Homepage / sign-up link per service, so users can go create an account.
const HOME = Object.fromEntries(TIP_SERVICES.map((s) => [s.key, s.home]));

/**
 * Editable list of tip links — same dropdown + "add another" pattern as ContactsField.
 * Value is an array of { key, value } rows; empty rows are allowed while editing.
 */
export default function TipLinksField({ value, onChange, testidPrefix = "tip" }) {
    const rows = useMemo(() => (value && value.length ? value : [{ key: "paypal", value: "" }]), [value]);

    const updateRow = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const addRow = () => onChange([...rows, { key: "paypal", value: "" }]);
    const removeRow = (i) => onChange(rows.length === 1 ? [{ key: "paypal", value: "" }] : rows.filter((_, idx) => idx !== i));

    return (
        <div data-testid={testidPrefix}>
            <div className="space-y-2">
                {rows.map((row, i) => (
                    <div key={i} className="flex gap-2 items-stretch" data-testid={`${testidPrefix}-row-${i}`}>
                        <div className="w-[160px] shrink-0">
                            <Select value={row.key} onValueChange={(v) => updateRow(i, { key: v })}>
                                <SelectTrigger data-testid={`${testidPrefix}-type-${i}`} className="h-10 rounded-none bg-[#141414] border-[#2a2a2a]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                                    {TIP_OPTIONS.map(([v, lbl]) => (
                                        <SelectItem key={v} value={v} className="pr-2">
                                            <span className="inline-flex items-center gap-2">
                                                {lbl}
                                                {HOME[v] && (
                                                    <a
                                                        href={HOME[v]}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        title={`Open ${lbl} to create an account`}
                                                        data-testid={`${testidPrefix}-home-${v}`}
                                                        className="text-[#8a8a8a] hover:text-[#16a34a] transition-colors"
                                                    >
                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                    </a>
                                                )}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Input
                            data-testid={`${testidPrefix}-value-${i}`}
                            value={row.value}
                            onChange={(e) => updateRow(i, { value: e.target.value })}
                            placeholder={PLACEHOLDER[row.key] || "username or full link"}
                            className="h-10 rounded-none bg-[#141414] border-[#2a2a2a] flex-1"
                        />
                        <button
                            type="button"
                            data-testid={`${testidPrefix}-remove-${i}`}
                            onClick={() => removeRow(i)}
                            className="h-10 w-10 shrink-0 border border-[#2a2a2a] text-[#666] hover:text-[#ff4500] hover:border-[#ff4500] flex items-center justify-center transition-colors"
                            aria-label="Remove tip link"
                            title={rows.length === 1 ? "Clear this tip link" : "Remove this tip link"}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                data-testid={`${testidPrefix}-add`}
                onClick={addRow}
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#16a34a] hover:underline"
            >
                <Plus className="h-3 w-3" /> Add another tip link
            </button>
        </div>
    );
}

// --- conversion between the array rows and the stored tip_links map ---
export function linksToRows(links) {
    if (!links) return [];
    const out = [];
    for (const s of TIP_SERVICES) {
        const v = (links[s.key] || "").trim();
        if (v) out.push({ key: s.key, value: v });
    }
    const other = (links.other_url || "").trim();
    if (other) out.push({ key: "other", value: other });
    return out;
}

export function rowsToLinks(rows) {
    const map = { paypal: "", venmo: "", cashapp: "", kofi: "", bmac: "", other_label: "", other_url: "" };
    for (const r of rows || []) {
        const v = (r.value || "").trim();
        if (!v) continue;
        if (r.key === "other") { map.other_url = v; if (!map.other_label) map.other_label = "Tip"; }
        else map[r.key] = v;
    }
    return map;
}
